import React, { createContext, useContext, useState, useEffect } from 'react';
import { getSupabaseClient } from '../supabaseClient';

const BusinessContext = createContext();

export const BusinessProvider = ({ children }) => {
  const [businessType, setBusinessType] = useState(() => {
    // Initialize from localStorage, checking both possible keys
    return localStorage.getItem('business_type') || 
           localStorage.getItem('businessType') || 
           null; // Start with null until we determine the correct business type
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableBusinessTypes, setAvailableBusinessTypes] = useState([]);

  // Static business types - matches your system_business_types table and database enum
  const loadAvailableBusinessTypes = async () => {
    try {
      const businessTypes = [
        { name: 'laundromat', friendly_name: 'Laundromat', active: true },
        { name: 'cleaning', friendly_name: 'Cleaning Service', active: true },
        { name: 'short_term_rentals', friendly_name: 'Short-Term Rentals', active: true },
        { name: 'self_storage', friendly_name: 'Self Storage', active: true },
        { name: 'property_management', friendly_name: 'Property Management', active: true }
      ];

      setAvailableBusinessTypes(businessTypes);
      return businessTypes;
    } catch (err) {
      console.error('Error loading business types:', err);
      setError(err.message);
      return [];
    }
  };

  // Try to determine business type from authenticated user
  const determineBusinessTypeFromAuth = async () => {
    try {
      const supabase = getSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();

      if (session?.user) {
        // Try to get business type from account data
        const { data: accountData, error: accountError } = await supabase
          .from('accounts')
          .select('business_type')
          .eq('id', session.user.id)
          .single();

        if (!accountError && accountData?.business_type) {
          console.log('Business type found from account:', accountData.business_type);
          return accountData.business_type;
        } else if (accountError) {
          console.log('Error fetching business type from account:', accountError);
        }
      }

      return null;
    } catch (error) {
      console.error('Error determining business type from auth:', error);
      return null;
    }
  };

  useEffect(() => {
    const initializeBusinessType = async () => {
      try {
        // Load available business types
        await loadAvailableBusinessTypes();

        // Check if we have a stored business type
        const storedType = localStorage.getItem('business_type') || 
                          localStorage.getItem('businessType');

        if (storedType) {
          setBusinessType(storedType);
          // Ensure both localStorage keys are set
          localStorage.setItem('business_type', storedType);
          localStorage.setItem('businessType', storedType);
          setLoading(false);
          return;
        }

        // Try to determine from authenticated user
        const authBusinessType = await determineBusinessTypeFromAuth();
        if (authBusinessType) {
          setBusinessType(authBusinessType);
          localStorage.setItem('business_type', authBusinessType);
          localStorage.setItem('businessType', authBusinessType);
          setLoading(false);
          return;
        }

        // Only default to laundromat if we truly can't determine the business type
        // This prevents overriding users with other business types
        if (!businessType) {
          console.log('No business type determined, defaulting to laundromat');
          setBusinessType('laundromat');
          localStorage.setItem('business_type', 'laundromat');
          localStorage.setItem('businessType', 'laundromat');
        }
      } catch (err) {
        console.error('Error in business type initialization:', err);
        setError(err.message);
        // Only fallback to laundromat if we have no business type set
        if (!businessType) {
          setBusinessType('laundromat');
          localStorage.setItem('business_type', 'laundromat');
          localStorage.setItem('businessType', 'laundromat');
        }
      } finally {
        setLoading(false);
      }
    };

    initializeBusinessType();
  }, []);

  // Update localStorage whenever businessType changes
  useEffect(() => {
    if (businessType) {
      localStorage.setItem('business_type', businessType);
      localStorage.setItem('businessType', businessType);
    }
  }, [businessType]);

  const isKnownBusinessType = (type) => {
    if (!type) return false;
    return availableBusinessTypes.some(bt => bt.name === type);
  };

  const value = {
    businessType,
    setBusinessType,
    availableBusinessTypes,
    isKnownBusinessType,
    loading,
    error
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
};

export const useBusinessType = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusinessType must be used within a BusinessProvider');
  }
  return context;
};